import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Heading,
  VStack,
  HStack,
  Button,
  Text,
  Dialog,
  Field,
  Input,
  NativeSelect,
  Badge,
  Spinner,
  useDisclosure,
  Checkbox,
  Avatar,
  IconButton,
} from '@chakra-ui/react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { usersAPI, organizationsAPI } from '../services/api';
import { User, Organization } from '../types';
import { toast } from '../utils/toast';
import { resolveLogoUrl } from '../utils/logoUrl';

const AdminPanel: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState<User[]>([]);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [userLoading, setUserLoading] = useState(false);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);
  const [sendingResetToId, setSendingResetToId] = useState<string | null>(null);
  const [sendingVerifyToId, setSendingVerifyToId] = useState<string | null>(null);
  const [uploadingOrgLogoId, setUploadingOrgLogoId] = useState<string | null>(null);
  
  // User dialog
  const { open: userDialogOpen, onOpen: onUserDialogOpen, onClose: onUserDialogClose } = useDisclosure();
  const { open: deleteDialogOpen, onOpen: onDeleteDialogOpen, onClose: onDeleteDialogClose } = useDisclosure();
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [userForm, setUserForm] = useState<{
    name: string;
    email: string;
    password: string;
    role: 'masterAdmin' | 'admin' | 'endUser';
    organizations: { organizationId: string; role: 'admin' | 'member' }[];
  }>({
    name: '',
    email: '',
    password: '',
    role: 'endUser',
    organizations: [],
  });

  // Organization dialog
  const { open: orgDialogOpen, onOpen: onOrgDialogOpen, onClose: onOrgDialogClose } = useDisclosure();
  const [orgForm, setOrgForm] = useState({
    name: '',
    description: '',
  });


  // Check if user is master admin or admin
  const isMasterAdmin = user?.role === 'masterAdmin' || user?.email === 'admin@admin.com';
  const isAdmin = user?.role === 'admin';
  const currentUserAvatarSrc = resolveLogoUrl(user?.avatarUrl || null);

  useEffect(() => {
    if (!isMasterAdmin && !isAdmin) {
      navigate('/dashboard');
      return;
    }
    loadData();
  }, [isMasterAdmin, isAdmin, navigate]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [usersResponse, orgsResponse] = await Promise.all([
        usersAPI.getAll(),
        organizationsAPI.getAll(isMasterAdmin), // Master Admin vidi sve organizacije
      ]);
      setUsers(Array.isArray(usersResponse?.data) ? usersResponse.data : []);
      setOrganizations(Array.isArray(orgsResponse?.data) ? orgsResponse.data : []);
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Failed to load data',
        status: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteOrganization = async (orgId: string, orgName: string) => {
    if (
      !window.confirm(
        `Are you sure you want to delete organization "${orgName}"? Projects for this organization must be archived or deleted first.`
      )
    ) {
      return;
    }
    try {
      await organizationsAPI.delete(orgId);
      toast({
        title: 'Success',
        description: 'Organization deleted',
        status: 'success',
      });
      await loadData();
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Failed to delete organization',
        status: 'error',
      });
    }
  };

  const handleUploadOrganizationLogo = async (orgId: string, file?: File) => {
    if (!file) return;
    setUploadingOrgLogoId(orgId);
    try {
      const response = await organizationsAPI.uploadLogo(orgId, file);
      const updatedOrg = response.data as Organization;
      setOrganizations((list) => list.map((o) => (o._id === orgId ? { ...o, ...updatedOrg } : o)));
      toast({ title: 'Success', description: 'Organization logo uploaded', status: 'success' });
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Failed to upload logo',
        status: 'error',
      });
    } finally {
      setUploadingOrgLogoId(null);
    }
  };

  const handleCreateUser = () => {
    setSelectedUser(null);
    setUserForm({ name: '', email: '', password: '', role: 'endUser', organizations: [] });
    onUserDialogOpen();
  };

  const norm = (id: string | { toString(): string } | undefined) => (id == null ? '' : String(id));

  const handleEditUser = (user: User) => {
    setSelectedUser(user);
    const orgs = (user as any).organizations?.map((o: any) => ({
      organizationId: norm(o.organization?._id ?? o.organization),
      role: (o.role === 'admin' ? 'admin' : 'member') as 'admin' | 'member',
    })).filter((o: { organizationId: string }) => o.organizationId) || [];
    setUserForm({
      name: user.name,
      email: user.email,
      password: '',
      role: (user.role as 'masterAdmin' | 'admin' | 'endUser') || 'endUser',
      organizations: orgs,
    });
    onUserDialogOpen();
  };

  const setOrganizationChecked = (orgId: string, checked: boolean, role: 'admin' | 'member' = 'member') => {
    const id = norm(orgId);
    if (!id) return;
    setUserForm((prev) => {
      const has = prev.organizations.some((o) => norm(o.organizationId) === id);
      if (checked && !has) {
        return {
          ...prev,
          organizations: [...prev.organizations.filter((o) => norm(o.organizationId) !== id), { organizationId: id, role }],
        };
      }
      if (!checked) {
        return {
          ...prev,
          organizations: prev.organizations.filter((o) => norm(o.organizationId) !== id),
        };
      }
      return prev;
    });
  };

  const setUserOrgRole = (orgId: string, role: 'admin' | 'member') => {
    const id = norm(orgId);
    setUserForm({
      ...userForm,
      organizations: userForm.organizations.map((o) =>
        norm(o.organizationId) === id ? { ...o, role } : o
      ),
    });
  };

  const handleSaveUser = async () => {
    if (!userForm.name.trim() || !userForm.email.trim()) {
      toast({
        title: 'Error',
        description: 'Name and email are required',
        status: 'error',
      });
      return;
    }

    if (!selectedUser && !userForm.password.trim()) {
      toast({
        title: 'Error',
        description: 'Password is required for new users',
        status: 'error',
      });
      return;
    }

    setUserLoading(true);
    try {
      if (selectedUser) {
        const updateData: any = {
          name: userForm.name,
          email: userForm.email,
          role: userForm.role,
          organizations: userForm.organizations,
        };
        if (userForm.password.trim()) {
          updateData.password = userForm.password;
        }
        await usersAPI.update(selectedUser.id || selectedUser._id || '', updateData);
        toast({
          title: 'Success',
          description: 'User updated successfully',
          status: 'success',
        });
      } else {
        await usersAPI.create(userForm.name, userForm.email, userForm.password, userForm.role, userForm.organizations);
        toast({
          title: 'Success',
          description: 'User created successfully',
          status: 'success',
        });
      }
      onUserDialogClose();
      loadData();
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Failed to save user',
        status: 'error',
      });
    } finally {
      setUserLoading(false);
    }
  };

  const handleDeleteUserClick = (user: User) => {
    setUserToDelete(user);
    onDeleteDialogOpen();
  };

  const handleSendVerificationLink = async (u: User) => {
    const userId = (u.id || (u as any)._id || '').toString();
    const isVerified = (u as any).emailVerified;
    if (!userId || isVerified) return;
    setSendingVerifyToId(userId);
    try {
      await usersAPI.sendVerificationLink(userId);
      toast({
        title: 'Success',
        description: "Verification link has been sent to the user's email.",
        status: 'success',
      });
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Sending link failed.',
        status: 'error',
      });
    } finally {
      setSendingVerifyToId(null);
    }
  };

  const handleSendResetLink = async (u: User) => {
    const userId = (u.id || (u as any)._id || '').toString();
    if (!userId) return;
    setSendingResetToId(userId);
    try {
      await usersAPI.sendResetLink(userId);
      toast({
        title: 'Success',
        description: 'Password reset link has been sent to the user\'s email.',
        status: 'success',
      });
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Sending link failed.',
        status: 'error',
      });
    } finally {
      setSendingResetToId(null);
    }
  };

  const handleDeleteUser = async () => {
    if (!userToDelete) return;

    setUserLoading(true);
    try {
      await usersAPI.delete(userToDelete.id || userToDelete._id || '');
      toast({
        title: 'Success',
        description: 'User deleted successfully',
        status: 'success',
      });
      setUserToDelete(null);
      onDeleteDialogClose();
      loadData();
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Failed to delete user',
        status: 'error',
      });
    } finally {
      setUserLoading(false);
    }
  };

  const handleCreateOrganization = () => {
    setOrgForm({ name: '', description: '' });
    onOrgDialogOpen();
  };

  const handleSaveOrganization = async () => {
    if (!orgForm.name.trim()) {
      toast({
        title: 'Error',
        description: 'Organization name is required',
        status: 'error',
      });
      return;
    }

    setUserLoading(true);
    try {
      await organizationsAPI.create(orgForm.name, orgForm.description);
      toast({
        title: 'Success',
        description: 'Organization created successfully',
        status: 'success',
      });
      onOrgDialogClose();
      loadData();
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Failed to create organization',
        status: 'error',
      });
    } finally {
      setUserLoading(false);
    }
  };

  const getDisplayRole = (u: User) => (u.email === 'admin@admin.com' ? 'masterAdmin' : (u.role || 'endUser'));
  const getRoleLabel = (role: string) => ({ masterAdmin: 'Master Admin', admin: 'Admin', endUser: 'End User' }[role] || role);

  const getRoleBadgeColor = (role?: string) => {
    switch (role) {
      case 'masterAdmin':
        return 'purple';
      case 'admin':
        return 'blue';
      case 'endUser':
        return 'gray';
      default:
        return 'gray';
    }
  };

  if (loading) {
    return (
      <Box minH="100vh" display="flex" alignItems="center" justifyContent="center" bg="gray.50">
        <Spinner size="xl" color="purple.500" />
      </Box>
    );
  }

  return (
    <Box minH="100vh" bg="gray.50">
      {/* Desktop sidebar */}
      <Box
        display={{ base: 'none', md: 'flex' }}
        position="fixed"
        top={0}
        left={0}
        h="100vh"
        w="260px"
        bg="white"
        borderRightWidth={1}
        borderColor="gray.200"
        boxShadow="sm"
        p={4}
        flexDirection="column"
        zIndex={15}
        overflow="hidden"
      >
        <Box
          position="absolute"
          inset={0}
          opacity={0.06}
          pointerEvents="none"
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <img
            src="/NS_Projects.png"
            alt="NS Projects watermark"
            style={{ maxWidth: '260px' }}
          />
        </Box>

        <Box
          position="relative"
          zIndex={1}
          display="flex"
          flexDirection="column"
          h="100%"
        >
          <Heading
            size="lg"
            mb={2}
            bgGradient="to-r"
            gradientFrom="#273f96"
            gradientTo="#1369b3"
            style={{ WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}
          >
            Northstar Projects
          </Heading>
          <HStack gap={2} mb={6}>
            {currentUserAvatarSrc ? (
              <img
                src={currentUserAvatarSrc}
                alt={user?.name || 'User avatar'}
                style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '9999px',
                  objectFit: 'cover',
                  border: '1px solid #e5e7eb',
                }}
              />
            ) : (
              <Avatar.Root size="sm">
                <Avatar.Fallback bg="gray.200" color="black">
                  {user?.name?.charAt(0).toUpperCase() || 'U'}
                </Avatar.Fallback>
              </Avatar.Root>
            )}
            <Text fontWeight="medium" color="gray.700">
              {user?.name}
            </Text>
          </HStack>
          <VStack align="stretch" gap={3} flex="1">
            <Button
              variant="outline"
              onClick={() => navigate('/dashboard')}
              borderRadius="lg"
              _hover={{
                transform: 'translateY(-2px)',
                boxShadow: 'md',
              }}
              justifyContent="flex-start"
              width="100%"
            >
              Dashboard
            </Button>
          </VStack>
          <VStack
            align="stretch"
            gap={2}
            pt={3}
            borderTopWidth={1}
            borderColor="gray.100"
          >
            {(isMasterAdmin || isAdmin) && (
              <Button
                variant="solid"
                colorScheme="purple"
                borderRadius="lg"
                justifyContent="flex-start"
                width="100%"
              >
                Admin Panel
              </Button>
            )}
            <Button
              variant="outline"
              onClick={() => navigate('/settings')}
              borderRadius="lg"
              _hover={{ bg: 'gray.100' }}
              justifyContent="flex-start"
              width="100%"
            >
              Settings
            </Button>
            <Button
              variant="outline"
              onClick={logout}
              borderRadius="lg"
              _hover={{
                bg: 'gray.100',
              }}
              justifyContent="flex-start"
              width="100%"
            >
              Logout
            </Button>
          </VStack>
        </Box>
      </Box>

      {/* Header */}
      <Box bg="white" borderBottomWidth={1} borderColor="gray.200" boxShadow="sm" mb={6}>
        <Container maxW="container.xl" py={4}>
          <HStack justifyContent="space-between">
            <HStack gap={3} alignItems="center">
              <IconButton
                aria-label="Back to dashboard"
                variant="ghost"
                borderRadius="full"
                onClick={() => navigate('/dashboard')}
              >
                <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </IconButton>
              <Heading size="xl" color="gray.800">
                Admin Panel
              </Heading>
            </HStack>
          </HStack>
        </Container>
      </Box>

      <Container
        maxW={{ base: 'container.xl', md: '100%' }}
        py={8}
        pl={{ base: 0, md: '280px' }}
        pr={{ base: 0, md: 8 }}
      >
        <VStack gap={8} align="stretch">
          {/* Users Section */}
          <Box bg="white" borderRadius="lg" p={6} boxShadow="sm">
            <HStack justifyContent="space-between" mb={6}>
              <Heading size="lg" color="gray.800">
                Users {isAdmin && !isMasterAdmin && '(End Users only)'}
              </Heading>
              <Button
                bgGradient="to-r"
                gradientFrom="#273f96"
                gradientTo="#1369b3"
                color="white"
                onClick={handleCreateUser}
                _hover={{
                  transform: 'translateY(-2px)',
                  boxShadow: 'xl',
                }}
              >
                + Add User
              </Button>
            </HStack>

            <Box overflowX="auto">
              <Box as="table" width="100%" borderCollapse="collapse">
                <Box as="thead" bg="gray.50">
                  <Box as="tr" borderBottomWidth={1} borderColor="gray.200">
                    <Box as="th" px={4} py={3} textAlign="left" fontWeight="semibold" fontSize="sm" color="gray.700">
                      Name
                    </Box>
                    <Box as="th" px={4} py={3} textAlign="left" fontWeight="semibold" fontSize="sm" color="gray.700">
                      Email
                    </Box>
                    <Box as="th" px={4} py={3} textAlign="left" fontWeight="semibold" fontSize="sm" color="gray.700">
                      Role
                    </Box>
                    <Box as="th" px={4} py={3} textAlign="left" fontWeight="semibold" fontSize="sm" color="gray.700">
                      Created
                    </Box>
                    <Box as="th" px={4} py={3} textAlign="left" fontWeight="semibold" fontSize="sm" color="gray.700">
                      Actions
                    </Box>
                  </Box>
                </Box>
                <Box as="tbody">
                  {(Array.isArray(users) ? users : []).map((u) => {
                    const userRowId = (u.id || (u as any)._id || '').toString();
                    const isSameUser = user?.id && userRowId === user.id;
                    const isMaster = getDisplayRole(u) === 'masterAdmin';
                    const isVerified = (u as any).emailVerified;
                    return (
                    <Box
                      as="tr"
                      key={userRowId}
                      borderBottomWidth={1}
                      borderColor="gray.200"
                      _hover={{ bg: 'gray.50' }}
                    >
                      <Box as="td" px={4} py={3}>
                        {u.name}
                      </Box>
                      <Box as="td" px={4} py={3}>
                        {u.email}
                      </Box>
                      <Box as="td" px={4} py={3}>
                        <Badge colorScheme={getRoleBadgeColor(getDisplayRole(u))}>
                          {getRoleLabel(getDisplayRole(u))}
                        </Badge>
                      </Box>
                      <Box as="td" px={4} py={3}>
                        {u.createdAt ? new Date(u.createdAt).toLocaleDateString() : '-'}
                      </Box>
                      <Box as="td" px={4} py={3}>
                        <HStack gap={2}>
                          {isMasterAdmin && (
                            <>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleEditUser(u)}
                                disabled={isMaster && !isSameUser}
                              >
                                Edit
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                colorScheme={isVerified ? 'gray' : 'green'}
                                onClick={() => handleSendVerificationLink(u)}
                                disabled={isMaster || userLoading || sendingVerifyToId === userRowId || isVerified}
                                loading={sendingVerifyToId === userRowId}
                              >
                                {isVerified ? 'Email verified' : 'Send verification link'}
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                colorScheme="blue"
                                onClick={() => handleSendResetLink(u)}
                                disabled={isMaster || userLoading || sendingResetToId === userRowId}
                                loading={sendingResetToId === userRowId}
                              >
                                Send password reset link
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                colorScheme="red"
                                onClick={() => handleDeleteUserClick(u)}
                                disabled={getDisplayRole(u) === 'masterAdmin' || userLoading}
                              >
                                Delete
                              </Button>
                            </>
                          )}
                        </HStack>
                      </Box>
                    </Box>
                  );
                  })}
                </Box>
              </Box>
            </Box>
          </Box>

          {/* Organizations Section - Only for Master Admin */}
          {isMasterAdmin && (
            <Box bg="white" borderRadius="lg" p={6} boxShadow="sm">
              <HStack justifyContent="space-between" mb={6}>
                <Heading size="lg" color="gray.800">Organizations</Heading>
                <Button
                  bgGradient="to-r"
                  gradientFrom="#273f96"
                  gradientTo="#1369b3"
                  color="white"
                  onClick={handleCreateOrganization}
                  _hover={{
                    transform: 'translateY(-2px)',
                    boxShadow: 'xl',
                  }}
                >
                  + Create Organization
                </Button>
              </HStack>

            <VStack gap={4} align="stretch">
              {organizations.length === 0 ? (
                <Text color="gray.500" textAlign="center" py={8}>
                  No organizations yet
                </Text>
              ) : (
                (Array.isArray(organizations) ? organizations : []).map((org) => {
                  const ownerObj = typeof org.owner === 'object' ? (org.owner as any) : null;
                  const ownerName = ownerObj?.name || 'Unknown';
                  const ownerId = ownerObj?._id || ownerObj?.id;
                  const canDeleteOrg = !!(
                    isMasterAdmin ||
                    (user?.id && ownerId && ownerId.toString() === user.id)
                  );
                  return (
                    <Box
                      key={org._id}
                      p={4}
                      borderWidth={1}
                      borderColor="gray.200"
                      borderRadius="md"
                      _hover={{ borderColor: 'blue.300', boxShadow: 'sm' }}
                    >
                      <HStack justifyContent="space-between" alignItems="flex-start">
                        <HStack alignItems="flex-start" gap={3}>
                          {resolveLogoUrl(org.logoUrl) ? (
                            <img
                              src={resolveLogoUrl(org.logoUrl) || ''}
                              alt={`${org.name} logo`}
                              style={{
                                width: '32px',
                                height: '32px',
                                borderRadius: '9999px',
                                objectFit: 'cover',
                                border: '1px solid #e5e7eb',
                                flexShrink: 0,
                              }}
                            />
                          ) : (
                            <Box
                              w="32px"
                              h="32px"
                              borderRadius="full"
                              bg="gray.200"
                              color="black"
                              display="flex"
                              alignItems="center"
                              justifyContent="center"
                              fontSize="xs"
                              fontWeight="semibold"
                              flexShrink={0}
                            >
                              {org.name
                                .split(/\s+/)
                                .filter(Boolean)
                                .slice(0, 2)
                                .map((p) => p[0]?.toUpperCase())
                                .join('') || 'ORG'}
                            </Box>
                          )}
                          <VStack align="start" gap={1}>
                          <Text fontWeight="semibold" fontSize="lg">
                            {org.name}
                          </Text>
                          {org.description && (
                            <Text fontSize="sm" color="gray.600">
                              {org.description}
                            </Text>
                          )}
                          <Text fontSize="xs" color="gray.500">
                            Owner: {ownerName}
                          </Text>
                          </VStack>
                        </HStack>
                        <HStack>
                          <Button
                            as="label"
                            size="sm"
                            variant="outline"
                            cursor="pointer"
                            loading={uploadingOrgLogoId === org._id}
                          >
                            Logo
                            <input
                              type="file"
                              accept="image/*"
                              style={{ display: 'none' }}
                              onChange={(e) => {
                                const file = e.target.files?.[0];
                                e.currentTarget.value = '';
                                handleUploadOrganizationLogo(org._id, file);
                              }}
                            />
                          </Button>
                          {canDeleteOrg && (
                            <Button
                              size="sm"
                              variant="outline"
                              colorScheme="red"
                              onClick={() => handleDeleteOrganization(org._id, org.name)}
                            >
                              Delete
                            </Button>
                          )}
                        </HStack>
                      </HStack>
                    </Box>
                  );
                })
              )}
            </VStack>
            </Box>
          )}
        </VStack>
      </Container>

      {/* User Dialog */}
      <Dialog.Root open={userDialogOpen} onOpenChange={(e: { open: boolean }) => !e.open && onUserDialogClose()}>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content>
            <Dialog.Header>
              <Dialog.Title>{selectedUser ? 'Edit User' : 'Create User'}</Dialog.Title>
              <Dialog.CloseTrigger />
            </Dialog.Header>
            <Dialog.Body>
              <VStack gap={4}>
                <Field.Root required>
                  <Field.Label>Name</Field.Label>
                  <Input
                    value={userForm.name}
                    onChange={(e) => setUserForm({ ...userForm, name: e.target.value })}
                    placeholder="User name"
                  />
                </Field.Root>
                <Field.Root required>
                  <Field.Label>Email</Field.Label>
                  <Input
                    type="email"
                    value={userForm.email}
                    onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
                    placeholder="user@example.com"
                  />
                </Field.Root>
                <Field.Root required={!selectedUser}>
                  <Field.Label>Password {selectedUser && '(leave empty to keep current)'}</Field.Label>
                  <Input
                    type="password"
                    value={userForm.password}
                    onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                    placeholder="Password"
                  />
                </Field.Root>
                <Field.Root>
                  <Field.Label>Role</Field.Label>
                  <NativeSelect.Root disabled={isAdmin && !isMasterAdmin}>
                    <NativeSelect.Field
                      value={userForm.role}
                      onChange={(e: any) => {
                        if (!isAdmin || isMasterAdmin) {
                          setUserForm({ ...userForm, role: e.target.value });
                        }
                      }}
                    >
                      {isMasterAdmin ? (
                        <>
                          <option value="endUser">End User</option>
                          <option value="admin">Admin</option>
                          <option value="masterAdmin">Master Admin</option>
                        </>
                      ) : (
                        <option value="endUser">End User</option>
                      )}
                    </NativeSelect.Field>
                  </NativeSelect.Root>
                  {isAdmin && !isMasterAdmin && (
                    <Text fontSize="xs" color="gray.500" mt={1}>
                      Admin can only create End User accounts
                    </Text>
                  )}
                </Field.Root>
                {organizations.length > 0 && (
                  <Field.Root>
                    <Field.Label>Organizations</Field.Label>
                    <Text fontSize="xs" color="gray.500" mb={2}>User can belong to one or more organizations</Text>
                    <VStack gap={2} align="stretch">
                      {(Array.isArray(organizations) ? organizations : []).map((org) => {
                        const orgIdStr = norm(org._id);
                        const isSelected = userForm.organizations.some((o) => norm(o.organizationId) === orgIdStr);
                        const selEntry = userForm.organizations.find((o) => norm(o.organizationId) === orgIdStr);
                        return (
                          <HStack
                            key={orgIdStr}
                            justifyContent="space-between"
                            p={2}
                            borderRadius="md"
                            bg={isSelected ? 'purple.50' : 'gray.50'}
                            borderWidth={1}
                            borderColor={isSelected ? 'purple.200' : 'gray.200'}
                            cursor="pointer"
                            onClick={() => setOrganizationChecked(orgIdStr, !isSelected, selEntry?.role || 'member')}
                            _hover={{ bg: isSelected ? 'purple.100' : 'gray.100' }}
                          >
                            <Checkbox.Root checked={isSelected} pointerEvents="none" cursor="pointer">
                              <Checkbox.HiddenInput tabIndex={-1} aria-hidden />
                              <Checkbox.Control cursor="pointer" />
                              <Checkbox.Label ml={2}>{org.name}</Checkbox.Label>
                            </Checkbox.Root>
                            {isSelected && (
                              <NativeSelect.Root
                                width="100px"
                                size="sm"
                                onClick={(e: React.MouseEvent) => e.stopPropagation()}
                                onPointerDown={(e: React.PointerEvent) => e.stopPropagation()}
                              >
                                <NativeSelect.Field
                                  value={selEntry?.role || 'member'}
                                  onChange={(e: any) => setUserOrgRole(orgIdStr, e.target.value as 'admin' | 'member')}
                                >
                                  <option value="member">Member</option>
                                  <option value="admin">Admin</option>
                                </NativeSelect.Field>
                              </NativeSelect.Root>
                            )}
                          </HStack>
                        );
                      })}
                    </VStack>
                  </Field.Root>
                )}
              </VStack>
            </Dialog.Body>
            <Dialog.Footer>
              <HStack>
                <Button variant="ghost" onClick={onUserDialogClose}>
                  Cancel
                </Button>
                <Button
                  bgGradient="to-r"
                  gradientFrom="#273f96"
                  gradientTo="#1369b3"
                  color="white"
                  onClick={handleSaveUser}
                  loading={userLoading}
                >
                  {selectedUser ? 'Update' : 'Create'}
                </Button>
              </HStack>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </Dialog.Root>

      {/* Organization Dialog */}
      <Dialog.Root open={orgDialogOpen} onOpenChange={(e: { open: boolean }) => !e.open && onOrgDialogClose()}>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content>
            <Dialog.Header>
              <Dialog.Title>Create Organization</Dialog.Title>
              <Dialog.CloseTrigger />
            </Dialog.Header>
            <Dialog.Body>
              <VStack gap={4}>
                <Field.Root required>
                  <Field.Label>Organization Name</Field.Label>
                  <Input
                    value={orgForm.name}
                    onChange={(e) => setOrgForm({ ...orgForm, name: e.target.value })}
                    placeholder="Organization name"
                  />
                </Field.Root>
                <Field.Root>
                  <Field.Label>Description</Field.Label>
                  <Input
                    value={orgForm.description}
                    onChange={(e) => setOrgForm({ ...orgForm, description: e.target.value })}
                    placeholder="Organization description (optional)"
                  />
                </Field.Root>
              </VStack>
            </Dialog.Body>
            <Dialog.Footer>
              <HStack>
                <Button variant="ghost" onClick={onOrgDialogClose}>
                  Cancel
                </Button>
                <Button
                  bgGradient="to-r"
                  gradientFrom="#273f96"
                  gradientTo="#1369b3"
                  color="white"
                  onClick={handleSaveOrganization}
                  loading={userLoading}
                >
                  Create
                </Button>
              </HStack>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </Dialog.Root>

      {/* Delete Confirmation Dialog */}
      <Dialog.Root open={deleteDialogOpen} onOpenChange={(e: { open: boolean }) => !e.open && onDeleteDialogClose()}>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content>
            <Dialog.Header>
              <Dialog.Title>Delete User</Dialog.Title>
              <Dialog.CloseTrigger />
            </Dialog.Header>
            <Dialog.Body>
              <Text>
                Are you sure you want to delete user <strong>{userToDelete?.name}</strong> ({userToDelete?.email})?
                This action cannot be undone.
              </Text>
            </Dialog.Body>
            <Dialog.Footer>
              <HStack>
                <Button variant="ghost" onClick={onDeleteDialogClose}>
                  Cancel
                </Button>
                <Button
                  colorScheme="red"
                  onClick={handleDeleteUser}
                  loading={userLoading}
                >
                  Delete
                </Button>
              </HStack>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </Dialog.Root>
    </Box>
  );
};

export default AdminPanel;
